import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authGoogleExchange, authVerify2fa, setAdminToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Handles the redirect back from https://auth.emergentagent.com.
 * URL fragment carries `session_id=xxx`. We POST it to the backend
 * which allowlists against `admin_users` and either issues a JWT
 * outright (viewer w/o 2FA) or returns a 2FA challenge.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const NAVY = "#0A1628";
const GOLD = "#D4AF37";

export default function AdminAuthCallbackPage() {
  const navigate = useNavigate();
  const { setUser, refresh } = useAuth();
  const processed = useRef(false);

  const [stage, setStage] = useState("exchanging"); // 'exchanging' | 'otp' | 'error'
  const [challengeId, setChallengeId] = useState(null);
  const [phoneHint, setPhoneHint] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [emailShown, setEmailShown] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setError("No Google session id found. Please try again.");
      setStage("error");
      return;
    }

    (async () => {
      try {
        const r = await authGoogleExchange(sessionId);
        if (r.requires_2fa) {
          setChallengeId(r.challenge_id);
          setPhoneHint(r.phone_hint || "");
          setEmailShown(r.email || "");
          setStage("otp");
        } else if (r.token) {
          setAdminToken(r.token);
          setUser(r.user);
          await refresh();
          navigate("/admin", { replace: true });
        } else {
          setError("Unexpected response — please try again.");
          setStage("error");
        }
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "Google sign-in failed.");
        setStage("error");
      }
      // Scrub the session_id out of the URL so a refresh doesn't retry.
      window.history.replaceState({}, "", window.location.pathname);
    })();
  }, [navigate, setUser, refresh]);

  const submitOtp = async (e) => {
    e?.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await authVerify2fa(challengeId, code.trim());
      setAdminToken(r.token);
      setUser(r.user);
      await refresh();
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Incorrect code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={pageWrap} data-testid="admin-auth-callback">
      <div style={card}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>NEARNOW · GOOGLE SIGN-IN</div>
        <h1 style={h1}>
          {stage === "exchanging" && "SIGNING YOU IN…"}
          {stage === "otp" && "TWO-FACTOR CHECK"}
          {stage === "error" && "SIGN-IN BLOCKED"}
        </h1>

        {stage === "exchanging" && (
          <p style={{ color: "#5b5e69" }} data-testid="callback-exchanging">Verifying your Google session…</p>
        )}

        {stage === "otp" && (
          <form onSubmit={submitOtp}>
            <p style={{ color: "#5b5e69", marginBottom: 18 }}>
              Signed in as <strong>{emailShown}</strong>. Enter the 6-digit code we just texted to {phoneHint}.
            </p>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoFocus
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              data-testid="callback-otp-input"
              style={{ ...inputStyle, letterSpacing: 14, fontSize: 28, textAlign: "center", fontWeight: 700 }}
              placeholder="000000"
            />
            {error && <p data-testid="callback-error" style={errBox}>{error}</p>}
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              data-testid="callback-verify-button"
              style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
              {busy ? "Verifying…" : "VERIFY & SIGN IN →"}
            </button>
          </form>
        )}

        {stage === "error" && (
          <>
            <p data-testid="callback-error" style={errBox}>{error}</p>
            <button onClick={() => navigate("/admin/login")} style={btnPrimary} data-testid="callback-back-button">
              ← BACK TO SIGN-IN
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const pageWrap = { background: "#F8F9FA", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
const card = { background: "#fff", borderRadius: 18, padding: "40px 36px", maxWidth: 460, width: "100%", border: `2px solid ${GOLD}`, boxShadow: "0 30px 60px -25px rgba(10,22,40,0.25)" };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 34, color: NAVY, margin: "4px 0 22px" };
const inputStyle = { width: "100%", padding: "14px 16px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 16, marginBottom: 18, outline: "none", boxSizing: "border-box" };
const btnPrimary = { width: "100%", background: GOLD, color: NAVY, border: "none", padding: "16px 22px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 16, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", marginTop: 4 };
const errBox = { color: "#A32D2D", background: "#FDECEC", padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 14 };
